import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren
} from "react";
import type { WaTemplateSetup } from "features/waMessaging/types";

export type WaTemplateSetupMode = 'view' | 'edit' | 'create';

type WaTemplateSetupContextValue = {
    readonly open: boolean;
    readonly mode: WaTemplateSetupMode | null;
    readonly resource: WaTemplateSetup | undefined;
    readonly openView: (resource: WaTemplateSetup) => void;
    readonly openEdit: (resource: WaTemplateSetup) => void;
    readonly openCreate: () => void;
    readonly onClose: () => void;
};

const WaTemplateSetupContext = createContext<WaTemplateSetupContextValue>(
    {} as WaTemplateSetupContextValue
);

const WaTemplateSetupProvider = ({ children }: PropsWithChildren) => {
    const [mode, setMode] = useState<WaTemplateSetupMode | null>(null);
    const [resource, setResource] = useState<WaTemplateSetup | undefined>();
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const openView = useCallback((next: WaTemplateSetup) => {
        clearTimeout(timeoutRef.current);
        setResource(next);
        setMode('view');
    }, []);

    const openEdit = useCallback((next: WaTemplateSetup) => {
        clearTimeout(timeoutRef.current);
        setResource(next);
        setMode('edit');
    }, []);

    const openCreate = useCallback(() => {
        clearTimeout(timeoutRef.current);
        setResource(undefined);
        setMode('create');
    }, []);

    const onClose = useCallback(() => {
        setMode(null);
        timeoutRef.current = setTimeout(() => setResource(undefined), 300);
    }, []);

    const value = useMemo<WaTemplateSetupContextValue>(() => ({
        open: mode !== null,
        mode,
        resource,
        openView,
        openEdit,
        openCreate,
        onClose
    }), [mode, resource, openView, openEdit, openCreate, onClose]);

    return (
        <WaTemplateSetupContext.Provider value={value}>
            {children}
        </WaTemplateSetupContext.Provider>
    );
};

export const useWaTemplateSetupContext = () => useContext(WaTemplateSetupContext);

export default WaTemplateSetupProvider;
